/**
 * « En cours » — la feuille que l'élève compose, travaille et clôt
 * (Hedi, 5 octobre 2026).
 *
 * **L'élève travaille sur papier, et photographie chaque exercice.** Une photo
 * par exercice, comme sur la feuille du parent (`EntrainementDuParent`) : c'est
 * ce découpage qui permet au parent de juger exercice par exercice. Reprendre
 * une photo la **remplace** ; la retirer laisse la ligne sans photo.
 *
 * **Retirer un exercice** n'est possible que tant que le parent ne l'a pas
 * jugé : un verdict est écrit dans la maîtrise et ne se défait pas, et le
 * serveur le refuse de toute façon (422). Le bouton n'est donc pas proposé
 * plutôt que proposé puis refusé.
 *
 * **Clore ouvre tous les corrigés, et c'est sans retour** — l'exception à la
 * règle 2 arbitrée ce jour-là, limitée aux feuilles que l'élève compose. La
 * confirmation est **dans la page**, jamais `window.confirm` (muet dans la
 * coquille Android), et elle dit ce que le geste emporte : la feuille ne se
 * rouvre pas, et aucune photo ne s'envoie ensuite — une copie prise après avoir
 * lu le corrigé n'est plus une tentative.
 *
 * Aucun score, aucun pourcentage (§14.1) : des comptes d'exercices, et le
 * verdict du parent en mots quand il en a posé un.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { EntrainementOut, LigneDEntrainementOut } from "@/api/schema";
import {
  MON_ENTRAINEMENT,
  confirmationDeCloture,
  partagerMesFeuilles,
  verdictEnMots,
} from "@/child/monEntrainement";
import { uploadPhoto } from "@/photo/upload";
import { intituleDExercice } from "@/ui/cahiers";
import { EnonceImages, imagesDeLEnonce } from "@/ui/EnonceImage";
import { parChapitre } from "@/ui/feuille";
import { EtiquetteParascolaire } from "@/ui/Parascolaire";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

type Etat =
  | { quoi: "chargement" }
  | { quoi: "panne" }
  | { quoi: "pret"; feuille: EntrainementOut | null };

function erreurDe(echec: unknown, repli: string): string {
  return echec instanceof ApiError ? echec.message : repli;
}

export function MonEntrainementScreen() {
  const [etat, setEtat] = useState<Etat>({ quoi: "chargement" });

  const charger = useCallback(async () => {
    try {
      const reponse = await call("mesPropresEntrainements");
      setEtat({ quoi: "pret", feuille: partagerMesFeuilles(reponse.entrainements ?? []).enCours });
    } catch {
      setEtat({ quoi: "panne" });
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Une réponse de geste porte la feuille entière : on la pose telle quelle
  // plutôt que de tout relire. Les URL sont signées par le serveur.
  const poser = useCallback((feuille: EntrainementOut) => {
    setEtat({ quoi: "pret", feuille: feuille.closed_at ? null : feuille });
  }, []);

  if (etat.quoi === "chargement") return <Loading />;

  if (etat.quoi === "panne") {
    return (
      <Screen title="Mon entraînement">
        <ErrorNote>Je n'arrive pas à lire ton entraînement.</ErrorNote>
        <Button variant="quiet" onClick={() => void charger()}>
          Réessayer
        </Button>
      </Screen>
    );
  }

  const feuille = etat.feuille;
  const lignes = feuille?.lignes ?? [];

  if (!feuille || lignes.length === 0) {
    return (
      <Screen title="Mon entraînement">
        <Card>
          <Empty>Tu n'as pas d'entraînement en cours.</Empty>
          <p className="small">
            Compose-en un depuis <Link to={`${MON_ENTRAINEMENT}/cahiers`}>tes cahiers</Link>, ou{" "}
            <Link to={`${MON_ENTRAINEMENT}/generer`}>génère-le</Link> sur ce que tu as vu en classe.
          </p>
        </Card>
      </Screen>
    );
  }

  const photographiees = feuille.photographiees ?? 0;

  return (
    <Screen title="Mon entraînement">
      <p className="muted small">
        Fais ces exercices sur ton cahier et photographie chacun d'eux. {photographiees}{" "}
        photographié{photographiees > 1 ? "s" : ""} sur {lignes.length}.
      </p>

      {parChapitre(lignes).map((bloc) => (
        <Card key={`${bloc.ouvrageTitre}/${bloc.chapitre}`}>
          <p className="devoirs__entete">
            <strong>{bloc.titre}</strong>
            <span className="muted small"> · {bloc.ouvrageTitre}</span>
          </p>
          <ul className="devoirs__liste">
            {bloc.lignes.map((ligne) => (
              <Ligne key={ligne.ligne_id} ligne={ligne} ouvrage={bloc.ouvrageTitre} poser={poser} />
            ))}
          </ul>
        </Card>
      ))}

      <Cloture lignes={lignes.length} photographiees={photographiees} />
    </Screen>
  );
}

type Envoi = "repos" | "envoi" | { panne: string };

function Ligne({
  ligne,
  ouvrage,
  poser,
}: {
  ligne: LigneDEntrainementOut;
  /** Le titre du cahier, répété sur chaque énoncé : l'étiquette accompagne
   *  l'image partout où elle est rendue (`ui/parascolaire.test`). */
  ouvrage: string;
  poser: (feuille: EntrainementOut) => void;
}) {
  const [envoi, setEnvoi] = useState<Envoi>("repos");
  const enonces = imagesDeLEnonce(ligne.enonce_urls, ligne.enonce_url);
  const occupe = envoi === "envoi";

  const faire = async (geste: () => Promise<EntrainementOut>, repli: string) => {
    setEnvoi("envoi");
    try {
      poser(await geste());
      setEnvoi("repos");
    } catch (echec) {
      setEnvoi({ panne: erreurDe(echec, repli) });
    }
  };

  const photographier = (file: File) =>
    faire(async () => {
      const photo = await uploadPhoto(file);
      return call("photographierUnEntrainement", {
        params: { ligne_id: ligne.ligne_id },
        body: { key: photo.key },
      });
    }, "La photo n'est pas partie. Réessaie.");

  const retirerLaPhoto = () =>
    faire(
      () => call("retirerLaPhotoDEntrainement", { params: { ligne_id: ligne.ligne_id } }),
      "La photo n'a pas pu être retirée. Réessaie.",
    );

  const retirerLExercice = () =>
    faire(
      () => call("retirerUneLigneDeMonEntrainement", { params: { ligne_id: ligne.ligne_id } }),
      "L'exercice n'a pas pu être retiré. Réessaie.",
    );

  const prise = (libelle: string) => (
    <label className="devoirs__prise">
      <span className="muted small">{libelle}</span>
      <input
        className="input"
        type="file"
        accept="image/*"
        capture="environment"
        disabled={occupe}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void photographier(file);
        }}
      />
    </label>
  );

  return (
    <li className="devoirs__ligne">
      <p className="devoirs__entete">
        <strong>{intituleDExercice(ligne)}</strong>
        <span className="muted small"> · {ligne.competence}</span>
      </p>

      {enonces.length ? (
        <>
          <EtiquetteParascolaire nom={ouvrage} />
          <EnonceImages images={enonces} statement="" />
        </>
      ) : null}

      {ligne.photo_url ? (
        <ul className="devoirs__photos">
          <li>
            <a href={ligne.photo_url} target="_blank" rel="noreferrer">
              <img
                className="devoirs__photo"
                src={ligne.photo_url}
                alt="Ta réponse photographiée"
                loading="lazy"
              />
            </a>
            {prise("Reprendre la photo")}
            <Button variant="quiet" disabled={occupe} onClick={() => void retirerLaPhoto()}>
              Retirer la photo
            </Button>
          </li>
        </ul>
      ) : (
        prise("Photographie ta réponse sur le cahier.")
      )}

      {/* Le parent peut relire une ligne photographiée avant la clôture : son
          verdict s'affiche dès qu'il existe, et l'exercice ne se retire plus. */}
      {ligne.verdict ? (
        <p className="small">{verdictEnMots(ligne)}</p>
      ) : (
        <Button variant="quiet" disabled={occupe} onClick={() => void retirerLExercice()}>
          Retirer cet exercice
        </Button>
      )}

      {occupe ? <p className="muted small">Envoi…</p> : null}
      {typeof envoi === "object" ? <ErrorNote>{envoi.panne}</ErrorNote> : null}
    </li>
  );
}

function Cloture({ lignes, photographiees }: { lignes: number; photographiees: number }) {
  const navigate = useNavigate();
  const [etat, setEtat] = useState<"ferme" | "confirme" | "envoi">("ferme");
  const [panne, setPanne] = useState<string | null>(null);

  const clore = async () => {
    setEtat("envoi");
    setPanne(null);
    try {
      await call("cloreMonEntrainement");
      navigate(`${MON_ENTRAINEMENT}/termines`);
    } catch (echec) {
      setPanne(erreurDe(echec, "La feuille n'a pas pu être close. Réessaie."));
      setEtat("confirme");
    }
  };

  return (
    <Card>
      {etat === "ferme" ? (
        <Button onClick={() => setEtat("confirme")}>Clore et voir la correction</Button>
      ) : (
        <div className="mon-entrainement__confirmation">
          <p>{confirmationDeCloture(lignes, photographiees)}</p>
          <div className="screen__actions">
            <Button onClick={() => void clore()} disabled={etat === "envoi"}>
              {etat === "envoi" ? "Clôture…" : "Oui, clore et voir la correction"}
            </Button>
            <Button variant="quiet" onClick={() => setEtat("ferme")} disabled={etat === "envoi"}>
              Pas encore
            </Button>
          </div>
        </div>
      )}
      {panne ? <ErrorNote>{panne}</ErrorNote> : null}
    </Card>
  );
}
